import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { AxiosError } from 'axios';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  usePostUserPreference,
  usePostWhatsappVerification,
} from 'services/settings/hooks';
import { UserPreference } from 'services/settings/type';

interface VerifyWhatsappProps {
  open: boolean;
  preferenceData: UserPreference;
  onClose: () => void;
  onPreferenceUpdate: () => void;
}

const formSchema = z.object({
  otp: z.string().min(1, {
    message: 'Please enter OTP.',
  }),
});

export default function VerifyWhatsappDialog({
  open,
  preferenceData,
  onClose,
  onPreferenceUpdate,
}: VerifyWhatsappProps) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      otp: '',
    },
  });

  const { mutateAsync: postPreference, isPending } =
    usePostWhatsappVerification();
  const { mutate: updatePreference } = usePostUserPreference({
    onSuccess: () => {
      onPreferenceUpdate();
      onClose();
      form.reset();
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
      toast.error(t('otp_validation_failed_msg'));
    },
  });

  async function onSubmit({ otp }: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);
      await postPreference({ type: 'validate_otp', otp_value: otp });
      updatePreference(preferenceData);
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = error.response?.data.error;
        setValidationError(message);
      }
      setIsLoading(false);
    }
  }

  const onSendOTP = async () => {
    try {
      await postPreference({ type: 'send_otp' });
      setIsOtpSent(true);
    } catch (error) {
      console.error(error);
      toast.error(t('failed_to_send_otp_msg'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className='max-w-md px-4 py-6 sm:p-8'
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className='text-xl leading-tight text-center'>
            {t('verify_whatsapp')}
          </DialogTitle>
          {isOtpSent ? (
            <DialogDescription className='text-base leading-tight'>
              {t('whatsapp_otp_verification')}
            </DialogDescription>
          ) : (
            <DialogDescription className='sr-only'>
              Whatsapp Verification
            </DialogDescription>
          )}
        </DialogHeader>

        {isOtpSent ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='w-full mt-4 space-y-8'
            >
              <div className='space-y-2'>
                <FormField
                  control={form.control}
                  name='otp'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('otp')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            setValidationError('');
                            const { value } = e.target;
                            const numericRegex = /^\d{0,6}$/;
                            if (numericRegex.test(value)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {validationError && (
                  <p className='text-sm font-semibold text-red-500'>
                    {validationError}
                  </p>
                )}
              </div>

              <div className='flex justify-center gap-x-4'>
                <Button
                  type='submit'
                  className='px-6 h-9'
                  disabled={!form.formState.isValid || isLoading || isPending}
                >
                  {isLoading && (
                    <Loader2
                      className='w-4 h-4 mr-2 animate-spin'
                      role='progressbar'
                    />
                  )}{' '}
                  {t('verify')}
                </Button>
                <Button
                  type='button'
                  className='px-6 h-9 bg-rose-400 hover:bg-rose-400/90'
                  onClick={onClose}
                >
                  {t('cancel')}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className='mt-2 space-y-8'>
            <p className='font-medium leading-tight text-center'>
              {t('whatsapp_verify_proceed')}
            </p>

            <div className='flex items-center justify-center gap-x-4'>
              <Button
                onClick={onSendOTP}
                className='px-6 h-9'
                disabled={isPending}
              >
                {isPending && (
                  <Loader2
                    className='w-4 h-4 mr-2 animate-spin'
                    role='progressbar'
                  />
                )}
                {t('ok')}
              </Button>
              <Button
                onClick={onClose}
                className='px-6 h-9 bg-rose-400 hover:bg-rose-400/90'
                disabled={isPending}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
